/**
 * PersonalboqenSmartFields.jsx
 * Gemeinsame smarte Eingabekomponenten für Personalbogen (Public + Admin)
 * - IBANInput: 4er-Blöcke + BIC-Lookup + Validierung
 * - AddressAutocomplete: Photon/OSM Adresssuche
 * - KrankenkassenSelect: Durchsuchbare Krankenkassenliste
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  CheckCircle2, Loader2, AlertCircle, MapPin, Search, X,
} from 'lucide-react';

// ── IBAN Utilities ──────────────────────────────────────────────────────────

function formatIBAN(raw) {
  const clean = raw.replace(/\s+/g, '').toUpperCase();
  return clean.match(/.{1,4}/g)?.join(' ') || clean;
}

export function validateIBAN(iban) {
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  if (clean.length < 15 || clean.length > 34) return false;
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.split('').map(c => {
    const code = c.charCodeAt(0);
    return code >= 65 ? (code - 55).toString() : c;
  }).join('');
  let remainder = BigInt(0);
  for (let i = 0; i < numeric.length; i++) {
    remainder = (remainder * 10n + BigInt(numeric[i])) % 97n;
  }
  return remainder === 1n;
}

/** IBAN-Input mit Formatierung, Validierung & BIC-Lookup */
export function IBANInput({ value, onChange, onBICFound, labelSize = 'text-sm' }) {
  const [lookingUp, setLookingUp] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const debounceRef = useRef(null);

  // Beim ersten Rendern: bestehenden Wert validieren
  useEffect(() => {
    if (value) {
      const clean = value.replace(/\s+/g, '');
      if (clean.length >= 15) setIsValid(validateIBAN(clean));
    }
  }, []);

  const handleChange = async (e) => {
    const raw = e.target.value.replace(/[^A-Za-z0-9\s]/g, '');
    const formatted = formatIBAN(raw);
    const clean = formatted.replace(/\s+/g, '');
    onChange(formatted, clean);
    setIsValid(null);
    clearTimeout(debounceRef.current);
    if (clean.length >= 15) {
      debounceRef.current = setTimeout(async () => {
        const valid = validateIBAN(clean);
        setIsValid(valid);
        if (valid && clean.startsWith('DE') && clean.length === 22) {
          setLookingUp(true);
          try {
            const res = await fetch(
              `https://openiban.com/validate/${clean}?getBIC=true&validateBankCode=true`,
              { signal: AbortSignal.timeout(4000) }
            );
            const data = await res.json();
            if (data.valid && data.bankData) {
              onBICFound?.(data.bankData.bic || '', data.bankData.name || '');
            }
          } catch { /* silent */ }
          finally { setLookingUp(false); }
        }
      }, 600);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className={labelSize}>IBAN <span className="text-destructive">*</span></Label>
      <div className="relative">
        <input
          type="text"
          value={formatIBAN(value || '')}
          onChange={handleChange}
          placeholder="DE12 3456 7890 1234 5678 90"
          inputMode="text"
          autoComplete="off"
          className={`w-full h-10 px-3 pr-10 rounded-md border bg-background text-sm font-mono tracking-wider
            focus:outline-none focus:ring-2 focus:ring-primary transition-colors
            ${isValid === false ? 'border-red-500 bg-red-50/30 dark:bg-red-900/10 ring-1 ring-red-500' : ''}
            ${isValid === true ? 'border-green-400 bg-green-50/30 dark:bg-green-900/10' : ''}
            ${isValid === null ? 'border-border' : ''}`}
          data-testid="iban-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {lookingUp && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!lookingUp && isValid === true && <CheckCircle2 className="h-4 w-4 text-green-500" />}
          {!lookingUp && isValid === false && <AlertCircle className="h-4 w-4 text-red-500" />}
        </div>
      </div>
      {isValid === false && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5" />
          Ungültige IBAN – bitte prüfen.
        </p>
      )}
    </div>
  );
}

// ── Deutsche Krankenkassen (vollständig) ────────────────────────────────────
export const KRANKENKASSEN = [
  'AOK Baden-Württemberg', 'AOK Bayern', 'AOK Bremen/Bremerhaven',
  'AOK Hessen', 'AOK NordWest', 'AOK Nordost', 'AOK PLUS (Sachsen + Thüringen)',
  'AOK Rheinland/Hamburg', 'AOK Rheinland-Pfalz/Saarland', 'AOK Sachsen-Anhalt',
  'Techniker Krankenkasse (TK)', 'Barmer', 'DAK-Gesundheit',
  'KKH – Kaufmännische Krankenkasse', 'HEK – Hanseatische Krankenkasse',
  'hkk Krankenkasse', 'KNAPPSCHAFT', 'Knappschaft-Bahn-See', 'Knappschaft-Minijob Zentrale', 'Minijob-Zentrale (Knappschaft)', 'Bertelsmann BKK', 'BIG direkt gesund', 'BKK 24',
  'BKK B. Braun Melsungen AG', 'BKK Diakonie', 'BKK Deutsche Bank AG',
  'BKK EVM', 'BKK exklusiv', 'BKK firmus', 'BKK Freudenberg',
  'BKK GS Pharma', 'BKK HMR', 'BKK Linde', 'BKK Melitta Plus',
  'BKK Mhplus', 'BKK MTU', 'BKK ProVita', 'BKK PricewaterhouseCoopers',
  'BKK R+V', 'BKK Salzgitter', 'BKK Scheufelen', 'BKK Schwarzwald-Baar-Heuberg',
  'BKK Technoform', 'BKK TUI', 'BKK VBU', 'BKK Verbundplus',
  'BKK Voralb HELLER*LEUZE', 'BKK Werra-Meissner', 'BKK ZF & Partner',
  'Bosch BKK', 'Continentale Betriebskrankenkasse', 'Debeka BKK',
  'energie-BKK', 'Heimat Krankenkasse', 'Mhplus Betriebskrankenkasse',
  'Pronova BKK', 'R+V Betriebskrankenkasse', 'Salus BKK',
  'SBKK – Siemens-Betriebskrankenkasse', 'SECURVITA Krankenkasse',
  'Signal Iduna Betriebskrankenkasse', 'SKD BKK', 'Viactiv Krankenkasse',
  'WMF Betriebskrankenkasse',
  'IKK Brandenburg und Berlin', 'IKK classic', 'IKK gesund plus', 'IKK Südwest',
  'Actimonda krankenkasse', 'atlas BKK ahlmann', 'Bergische Krankenkasse',
  'BKK achenbach buschhütten', 'BKK Akzo Nobel Bayern', 'BKK Dürkopp Adler',
  'BKK Faber-Castell & Partner', 'BKK Herford Minden Ravensberg', 'BKK Mahle',
  'BKK Pfaff', 'BKK Pfalz', 'BKK Public', 'BKK Wittener',
  'Gemeinsame Betriebskrankenkasse Thüringen', 'Koenig & Bauer BKK',
  'Krones BKK', 'Landwirtschaftliche Krankenkasse (LKK)',
  'Mecklenburger BKK', 'SVLFG – Sozialversicherung f. Landwirtschaft',
  'Vereinigte IKK', 'Wieland BKK',
  'Sonstige / Private Krankenversicherung',
];

const SONSTIGE_VALUE = 'Sonstige / Private Krankenversicherung';

/** Durchsuchbare Krankenkassen-Select-Box */
export function KrankenkassenSelect({ value, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [sonstigeText, setSonstigeText] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const isSonstige = value === SONSTIGE_VALUE || (value && value.startsWith('Sonstige / Private'));

  const filtered = KRANKENKASSEN.filter(k =>
    k.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = (kk) => {
    onChange(kk);
    setOpen(false);
    setSearch('');
    if (kk !== SONSTIGE_VALUE) setSonstigeText('');
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={e => e.key === 'Enter' && handleOpen()}
        className={`flex items-center justify-between h-10 w-full rounded-md border bg-background px-3 text-sm cursor-pointer transition-colors hover:border-primary/50
          ${open ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
          ${value ? 'text-foreground' : 'text-muted-foreground'}`}
        data-testid="krankenkasse-select"
      >
        <span className="truncate">{value || 'Krankenkasse auswählen…'}</span>
        <Search className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
      </div>

      {open && (
        <div className="relative z-50">
          <div className="absolute w-full bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Krankenkasse suchen…"
                  className="w-full pl-8 pr-3 h-8 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary"
                  data-testid="krankenkasse-search"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto overscroll-contain">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">Keine Ergebnisse</div>
              ) : (
                filtered.map(kk => (
                  <button
                    key={kk}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); handleSelect(kk); }}
                    onTouchStart={() => handleSelect(kk)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-muted active:bg-muted/70 transition-colors
                      ${kk === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}
                      ${kk === SONSTIGE_VALUE ? 'border-t border-border mt-1 text-violet-600 dark:text-violet-400 font-medium' : ''}
                    `}
                  >
                    {kk}
                    {kk === value && <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {isSonstige && (
        <div className="space-y-1.5">
          <Label className="text-sm">Name der Krankenversicherung *</Label>
          <Input
            value={sonstigeText}
            onChange={e => {
              setSonstigeText(e.target.value);
              onChange(`${SONSTIGE_VALUE}: ${e.target.value}`);
            }}
            placeholder="z.B. Allianz Private Krankenversicherung"
            autoFocus
            data-testid="krankenkasse-sonstige-input"
          />
        </div>
      )}
    </div>
  );
}

// ── SV-Nummer Formatierung & Validierung ───────────────────────────────────

/** Formatiert SV-Nummer: 12 Zeichen → XX XXXXXX X XXX */
function formatSVNummer(raw) {
  const clean = raw.replace(/[\s\-]/g, '').toUpperCase();
  if (clean.length <= 2) return clean;
  if (clean.length <= 8) return `${clean.slice(0,2)} ${clean.slice(2)}`;
  if (clean.length <= 9) return `${clean.slice(0,2)} ${clean.slice(2,8)} ${clean.slice(8)}`;
  return `${clean.slice(0,2)} ${clean.slice(2,8)} ${clean.slice(8,9)} ${clean.slice(9,12)}`;
}

/** Prüft SV-Nummer auf Vollständigkeit und Format (keine Prüfziffer-Berechnung da nicht standardisiert) */
function validateSVNummer(sv) {
  const clean = sv.replace(/\s/g, '').toUpperCase();
  if (clean.length !== 12) return false;
  // Stellen 1-2: Ziffern
  if (!/^\d{2}/.test(clean)) return false;
  // Stellen 3-8: Geburtsdatum TTMMJJ
  const datePart = clean.slice(2, 8);
  if (!/^\d{6}$/.test(datePart)) return false;
  const day = parseInt(datePart.slice(0, 2));
  const month = parseInt(datePart.slice(2, 4));
  if (day < 1 || day > 31 || month < 1 || month > 12) return false;
  // Stelle 9: Buchstabe
  if (!/^[A-Z]$/.test(clean[8])) return false;
  // Stellen 10-12: Ziffern
  if (!/^\d{3}$/.test(clean.slice(9, 12))) return false;
  return true;
}

/** SV-Nummer Input mit Formatierung + Validierung */
export function SVNummerInput({ value, onChange, labelSize = 'text-sm', required = false }) {
  const [isValid, setIsValid] = useState(null);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^A-Za-z0-9\s]/g, '');
    const formatted = formatSVNummer(raw.replace(/\s/g, ''));
    onChange(formatted);
    const clean = formatted.replace(/\s/g, '');
    if (clean.length === 12) setIsValid(validateSVNummer(clean));
    else setIsValid(null);
  };

  const handleBlur = () => {
    const clean = (value || '').replace(/\s/g, '');
    if (clean.length > 0) setIsValid(validateSVNummer(clean));
  };

  return (
    <div className="space-y-1.5">
      <Label className={labelSize}>
        SV-Nummer{required && <span className="text-destructive ml-0.5">*</span>}
        <span className="text-muted-foreground font-normal ml-1 text-xs">(z.B. 12 123456 A 011)</span>
      </Label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="12 123456 A 011"
          maxLength={15}
          className={`w-full h-10 px-3 pr-9 rounded-md border bg-background text-sm font-mono tracking-wider
            focus:outline-none focus:ring-2 focus:ring-primary transition-colors
            ${isValid === false ? 'border-red-500 bg-red-50/30 ring-1 ring-red-500' : ''}
            ${isValid === true ? 'border-green-400 bg-green-50/30' : ''}
            ${isValid === null ? 'border-border' : ''}`}
          data-testid="sv-nummer-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isValid === true && <CheckCircle2 className="h-4 w-4 text-green-500" />}
          {isValid === false && <AlertCircle className="h-4 w-4 text-red-500" />}
        </div>
      </div>
      {isValid === false && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5" />
          Ungültige SV-Nummer (Format: TT TTMMJJ B XXX)
        </p>
      )}
    </div>
  );
}

// ── Steuer-ID Validierung (Modulo-11) ──────────────────────────────────────

function validateSteuerID(id) {
  const clean = id.replace(/\s/g, '');
  if (!/^\d{11}$/.test(clean)) return false;
  if (clean[0] === '0') return false;

  // Prüfziffernberechnung nach § 139b AO (Modulo 11)
  let product = 10;
  for (let i = 0; i < 10; i++) {
    let sum = (parseInt(clean[i]) + product) % 10;
    if (sum === 0) sum = 10;
    product = (2 * sum) % 11;
  }
  const checkDigit = (11 - product) % 10;
  return checkDigit === parseInt(clean[10]);
}

/** Steuer-ID Input mit Modulo-11 Prüfung */
export function SteuerIDInput({ value, onChange, labelSize = 'text-sm', required = false }) {
  const [isValid, setIsValid] = useState(null);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 11);
    onChange(raw);
    if (raw.length === 11) setIsValid(validateSteuerID(raw));
    else setIsValid(null);
  };

  const handleBlur = () => {
    const clean = (value || '').replace(/\D/g, '');
    if (clean.length > 0) setIsValid(validateSteuerID(clean));
  };

  return (
    <div className="space-y-1.5">
      <Label className={labelSize}>
        Steueridentifikationsnummer{required && <span className="text-destructive ml-0.5">*</span>}
        <span className="text-muted-foreground font-normal ml-1 text-xs">(11 Ziffern)</span>
      </Label>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="12345678901"
          maxLength={11}
          className={`w-full h-10 px-3 pr-9 rounded-md border bg-background text-sm font-mono tracking-wider
            focus:outline-none focus:ring-2 focus:ring-primary transition-colors
            ${isValid === false ? 'border-red-500 bg-red-50/30 ring-1 ring-red-500' : ''}
            ${isValid === true ? 'border-green-400 bg-green-50/30' : ''}
            ${isValid === null ? 'border-border' : ''}`}
          data-testid="steuer-id-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isValid === true && <CheckCircle2 className="h-4 w-4 text-green-500" />}
          {isValid === false && <AlertCircle className="h-4 w-4 text-red-500" />}
        </div>
      </div>
      {isValid === false && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5" />
          Ungültige Steuer-ID – bitte prüfen (11 Ziffern, Prüfziffer falsch)
        </p>
      )}
    </div>
  );
}

// ── CountrySelect – Durchsuchbares Länder-Dropdown ─────────────────────────

const COUNTRIES_DE = [
  // Häufig in Deutschland gewählt (oben)
  'Deutschland', 'Türkei', 'Polen', 'Russland', 'Ukraine', 'Kasachstan',
  'Rumänien', 'Italien', 'Griechenland', 'Österreich', 'Schweiz',
  'Kosovo', 'Serbien', 'Bosnien und Herzegowina', 'Nordmazedonien', 'Kroatien',
  'Bulgarien', 'Ungarn', 'Tschechien', 'Slowakei', 'Slowenien',
  // A–Z (alphabetisch)
  'Afghanistan', 'Ägypten', 'Albanien', 'Algerien', 'Angola', 'Argentinien',
  'Armenien', 'Aserbaidschan', 'Äthiopien', 'Australien', 'Bahrain',
  'Bangladesch', 'Belarus', 'Belgien', 'Bolivien', 'Brasilien',
  'Chile', 'China', 'Costa Rica', 'Dänemark', 'Dominikanische Republik',
  'Ecuador', 'El Salvador', 'Estland', 'Finnland', 'Frankreich',
  'Georgien', 'Ghana', 'Guatemala', 'Honduras',
  'Indien', 'Indonesien', 'Irak', 'Iran', 'Irland', 'Island', 'Israel',
  'Japan', 'Jemen', 'Jordanien', 'Kambodscha', 'Kamerun', 'Kanada',
  'Kenia', 'Kirgisistan', 'Kolumbien', 'Kuba', 'Kuwait',
  'Laos', 'Lettland', 'Libanon', 'Libyen', 'Liechtenstein', 'Litauen', 'Luxemburg',
  'Marokko', 'Mexiko', 'Moldawien', 'Monaco', 'Montenegro', 'Mosambik',
  'Nepal', 'Neuseeland', 'Niederlande', 'Nigeria', 'Norwegen',
  'Pakistan', 'Palästinensische Gebiete', 'Panama', 'Paraguay', 'Peru', 'Philippinen', 'Portugal',
  'Saudi-Arabien', 'Schweden', 'Senegal', 'Singapur',
  'Somalia', 'Spanien', 'Sri Lanka', 'Südafrika', 'Sudan', 'Syrien',
  'Tadschikistan', 'Thailand', 'Tunesien', 'Turkmenistan',
  'Uganda', 'Uruguay', 'USA', 'Usbekistan', 'Venezuela', 'Vietnam', 'Zypern',
  'Sonstige',
];

export function CountrySelect({
  value, onChange,
  placeholder = 'Land auswählen…',
  testId = 'country-select',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = COUNTRIES_DE.filter(c =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const handleSelect = (country) => {
    onChange(country);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        role="combobox"
        tabIndex={0}
        onClick={() => setOpen(v => !v)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(v => !v); } }}
        className={`flex items-center justify-between h-10 w-full rounded-md border bg-background px-3 text-sm cursor-pointer transition-colors
          ${!value ? 'text-muted-foreground' : 'text-foreground'}
          ${open ? 'ring-2 ring-primary border-primary' : 'border-border hover:border-muted-foreground/50'}`}
        data-testid={testId}
      >
        <span className="truncate">{value || placeholder}</span>
        <Search className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
      </div>
      {open && (
        <div className="relative z-50">
          <div className="absolute w-full bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Land suchen…"
                  className="w-full pl-8 pr-3 h-8 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary"
                  data-testid={`${testId}-search`}
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto overscroll-contain">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">Keine Ergebnisse</div>
              ) : (
                filtered.map(country => (
                  <button
                    key={country}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); handleSelect(country); }}
                    onTouchStart={() => handleSelect(country)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-muted active:bg-muted/70 transition-colors
                      ${country === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}
                      ${country === 'Sonstige' ? 'border-t border-border mt-1 text-violet-600 dark:text-violet-400 font-medium' : ''}
                    `}
                  >
                    {country}
                    {country === value && <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export function AddressAutocomplete({ strasse, hausnummer, plz, ort, onChange }) {
  const [query, setQuery] = useState(
    strasse ? `${strasse} ${hausnummer || ''}`.trim() : ''
  );
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilled, setIsFilled] = useState(!!(strasse && plz && ort));
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Klick außerhalb schließt die Liste
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 4) { setSuggestions([]); return; }
    setIsLoading(true);
    try {
      const res = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lang=de&countrycode=DE`,
        { signal: AbortSignal.timeout(3000) }
      );
      const data = await res.json();
      const results = (data.features || [])
        .filter(f => f.properties?.country === 'Deutschland' || f.properties?.country === 'Germany')
        .map(f => {
          const p = f.properties;
          const street = p.street || p.name || '';
          const housenum = p.housenumber || '';
          const postcode = p.postcode || '';
          const city = p.city || p.town || p.village || p.municipality || '';
          const display = [street, housenum, postcode, city].filter(Boolean).join(', ');
          return { display, street, housenum, postcode, city };
        })
        .filter(r => r.street && (r.postcode || r.city));
      const seen = new Set();
      const unique = results.filter(r => {
        const key = `${r.street}|${r.postcode}|${r.city}`;
        if (seen.has(key)) return false;
        seen.add(key); return true;
      });
      setSuggestions(unique.slice(0, 5));
      setShowSuggestions(unique.length > 0);
    } catch { /* Photon nicht erreichbar */ }
    finally { setIsLoading(false); }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setIsFilled(false);
    onChange({ strasse: '', hausnummer: '', plz: '', ort: '' });
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 420);
  };

  const handleSelect = (s) => {
    setQuery([s.street, s.housenum, s.postcode, s.city].filter(Boolean).join(', '));
    setShowSuggestions(false);
    setIsFilled(true);
    onChange({ strasse: s.street, hausnummer: s.housenum, plz: s.postcode, ort: s.city });
  };

  const handleClear = () => {
    setQuery('');
    setIsFilled(false);
    setSuggestions([]);
    onChange({ strasse: '', hausnummer: '', plz: '', ort: '' });
  };

  return (
    <div ref={containerRef} className="relative space-y-2">
      {/* Autocomplete-Suchfeld */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Adresse eingeben – Vorschläge erscheinen automatisch"
          autoComplete="off"
          className={`w-full pl-9 pr-9 h-10 rounded-md border bg-background text-sm transition-colors
            focus:outline-none focus:ring-2 focus:ring-primary
            ${isFilled ? 'border-green-400 bg-green-50/30 dark:bg-green-900/10' : 'border-border'}`}
          data-testid="address-autocomplete-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!isLoading && query && !isFilled && (
            <button type="button" onClick={handleClear} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
          {!isLoading && isFilled && <CheckCircle2 className="h-4 w-4 text-green-500" />}
        </div>
      </div>

      {/* Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-0 bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
              onTouchStart={() => handleSelect(s)}
              className="w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-muted active:bg-muted/70 transition-colors text-left"
            >
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">{[s.street, s.housenum].filter(Boolean).join(' ')}</p>
                <p className="text-xs text-muted-foreground">{[s.postcode, s.city].filter(Boolean).join(' ')}</p>
              </div>
            </button>
          ))}
          <div className="px-3 py-1.5 bg-muted/30 border-t border-border">
            <p className="text-[10px] text-muted-foreground/60">Powered by OpenStreetMap / Photon</p>
          </div>
        </div>
      )}

      {/* Manuelle Felder */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Straße *</Label>
          <Input value={strasse} onChange={e => onChange({ strasse: e.target.value })} placeholder="Straßenname" className="h-8 text-sm" data-testid="strasse-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Hausnr. *</Label>
          <Input value={hausnummer} onChange={e => onChange({ hausnummer: e.target.value })} placeholder="Nr." className="h-8 text-sm" data-testid="hausnummer-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">PLZ *</Label>
          <Input value={plz} onChange={e => onChange({ plz: e.target.value })} inputMode="numeric" placeholder="PLZ" className="h-8 text-sm" data-testid="plz-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Ort *</Label>
          <Input value={ort} onChange={e => onChange({ ort: e.target.value })} placeholder="Stadt" className="h-8 text-sm" data-testid="ort-input" />
        </div>
      </div>
    </div>
  );
}


// ── KV-Typ Select (Krankenversicherungstyp) ────────────────────────────────

export const KV_TYP_OPTIONS = [
  { value: 'pflicht',      label: 'Gesetzlich pflichtversichert (GKV)' },
  { value: 'freiwillig',   label: 'Freiwillig gesetzlich versichert (GKV)' },
  { value: 'studentisch',  label: 'Studentische Krankenversicherung (GKV)' },
  { value: 'familien',     label: 'Familienversichert (über Ehepartner/Eltern)' },
  { value: 'privat',       label: 'Privat versichert (PKV)' },
  { value: 'selbstzahler', label: 'Selbstzahler / keine Versicherung' },
  { value: 'befreit',      label: 'Befreit (z.B. Beamte, Pensionäre)' },
];

/** KV-Typ Dropdown – Krankenversicherungstyp für Lohnabrechnung */
export function KVTypSelect({ value, onChange, required = false, labelSize = 'text-sm' }) {
  return (
    <div className="space-y-1.5">
      <Label className={labelSize}>
        Krankenversicherungstyp{required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        data-testid="kv-typ-select"
      >
        <option value="">Bitte auswählen…</option>
        {KV_TYP_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// Deutsches Datumsfeld: Eingabe/Anzeige immer TT.MM.JJJJ, intern als ISO (YYYY-MM-DD) gespeichert.
// Ersetzt das native <input type="date">, das je nach Geräte-Sprache ein fremdes Format zeigt.
export function GermanDateInput({ value, onChange, className = '', placeholder = 'TT.MM.JJJJ', ...rest }) {
  const toDE = (iso) => {
    if (!iso) return '';
    const s = String(iso).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) return s;
    return '';
  };
  const [text, setText] = React.useState(toDE(value));
  React.useEffect(() => { setText(toDE(value)); }, [value]);

  const emit = (de) => {
    if (!de) { onChange(''); return; }
    const m = de.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const d = +m[1], mo = +m[2], y = +m[3];
      if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
        onChange(`${m[3]}-${m[2]}-${m[1]}`);
      }
    }
  };
  const handle = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let out = digits;
    if (digits.length >= 5) out = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4);
    else if (digits.length >= 3) out = digits.slice(0, 2) + '.' + digits.slice(2);
    setText(out);
    emit(out);
  };
  return (
    <Input type="text" inputMode="numeric" placeholder={placeholder} value={text}
      onChange={handle} maxLength={10} className={className} style={{ fontSize: '16px' }} {...rest} />
  );
}
